'use client';

import type { ReactNode } from 'react';
import { CheckmarkCircle02Icon } from '@terra/ui-icons';
import { Box, HStack } from '@terra/ui-styled-system';
import { Badge } from '../../../../../components/badge';
import { Text } from '../../../../../components/styled/text';
import { MarkdownPreview } from '../../../../markdown-preview';
import type { OutputRenderContext } from '../../tool-config-types';

const cleanContent = (content: string): string => {
  return content
    .replace(/<topic[^>]*>/gi, '')
    .replace(/<\/topic>/gi, '')
    .trim();
};

export const extractMemories = (content: unknown): string[] => {
  if (!content) return [];
  if (typeof content === 'object' && content !== null) {
    const obj = content as Record<string, unknown>;

    if (Array.isArray(obj.memories)) {
      const memories = obj.memories
        .map((m) => {
          if (typeof m === 'string') return cleanContent(m);
          if (
            typeof m === 'object' &&
            m !== null &&
            typeof (m as Record<string, unknown>).content !== 'undefined'
          ) {
            return cleanContent(
              String(
                (
                  m as {
                    content: unknown;
                  }
                ).content,
              ),
            );
          }

          return null;
        })
        .filter((m): m is string => m !== null && m.length > 0);

      if (memories.length > 0) return memories;
    }

    if (Array.isArray(content)) {
      const memories = content
        .map((m) => {
          if (typeof m === 'string') return cleanContent(m);
          if (
            typeof m === 'object' &&
            m !== null &&
            typeof (m as Record<string, unknown>).content !== 'undefined'
          ) {
            return cleanContent(
              String(
                (
                  m as {
                    content: unknown;
                  }
                ).content,
              ),
            );
          }

          return null;
        })
        .filter((m): m is string => m !== null && m.length > 0);

      if (memories.length > 0) return memories;
    }

    if (typeof obj.content === 'string') {
      return [cleanContent(obj.content)];
    }
  }

  if (typeof content === 'string' && content.trim().length > 0) {
    try {
      const parsed = JSON.parse(content);

      return extractMemories(parsed);
    } catch {
      const lines = content
        .split(/\n\n|\n/)
        .map((line) => cleanContent(line))
        .filter((line) => line.length > 0 && !/^\d+%$/.test(line));

      return lines;
    }
  }

  return [];
};

export const renderMemoryResult = (ctx: OutputRenderContext): ReactNode => {
  if (!ctx.content) return null;

  return (
    <HStack gap={1} alignItems="center">
      <Badge
        variant="outline"
        size="sm"
        icon={<CheckmarkCircle02Icon size={12} />}
      >
        <HStack gap={1} alignItems="center">
          {/* eslint-disable-next-line i18next/no-literal-string */}
          <Text fontSize="2xs">Saved memory</Text>
        </HStack>
      </Badge>
    </HStack>
  );
};

export const renderMemoryListResult = (ctx: OutputRenderContext): ReactNode => {
  const memories = extractMemories(ctx.content);
  const { maxHeight = '400px' } = ctx;

  if (memories.length === 0) {
    return null;
  }

  // Convert memories to markdown bullet list format
  const markdownContent = memories.map((memory) => `* ${memory}`).join('\n');

  return (
    <Box
      bg="bg.canvas"
      p={2}
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
      width="100%"
      maxHeight={maxHeight}
      overflow="auto"
      position="relative"
    >
      <MarkdownPreview source={markdownContent} fontSize={12} compact />
    </Box>
  );
};

export const renderMemoryInput = (input: unknown): ReactNode => {
  if (!input || typeof input !== 'object') return null;
  const obj = input as Record<string, unknown>;
  const query = obj.query;

  if (typeof query === 'string') {
    return (
      // eslint-disable-next-line i18next/no-literal-string
      <p className="text-xs text-muted-foreground italic">
        Searching: &quot;{query}&quot;
      </p>
    );
  }

  return null;
};
